import { css, cx } from "@emotion/css";
import { ExternalLink, DeprecatedIconButton, tableStyles, LegacyDataTable, LegacyDataTableBody, LegacyDataTableHeader, LegacyDataTableRow, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import { ExclamationCircleIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { InlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDApplicationResource, ArgoCDInstanceSummaryResource, EnvironmentV2Resource, ProjectResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import { ArgoCDInstanceLinks } from "~/areas/infrastructure/components/ArgoCDInstances/argoCDInstanceLinks";
import { ArgoCDApplicationDetails } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationDetails";
import { ArgoCDApplicationEnvironmentsTagList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationEnvironmentsTagList";
import { ArgoCDApplicationTenantsTagList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationTenantsTagList";
import { isApplicationMissingRequiredConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/configurationValidation";
import { QuickSkeleton } from "~/areas/tasks/components/Task/Kubernetes/components/QuickSkeleton";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
const styles = {
    headerRow: css({
        font: text.heading.xSmall,
        color: themeTokens.color.text.tertiary,
        margin: 0,
        textTransform: "uppercase",
        borderTop: "none !important",
    }),
    argoCDNameField: css({
        font: text.body.bold.medium,
        cursor: "pointer",
    }),
    navItemIcon: css({
        height: "20px",
        width: "100%",
    }),
    multiItemCellFlexContainer: css({
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "left",
        alignItems: "center",
        gap: space[32],
    }),
    indentedRow: css({
        paddingLeft: space[56],
    }),
    chipCell: css({
        alignItems: "center",
    }),
    applicationNameFlex: css({
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[16],
    }),
    applicationName: css({
        font: text.body.regular.medium,
    }),
    argoCDInstanceRow: css({
        backgroundColor: themeTokens.color.background.secondary.default,
    }),
    normalFont: css({
        fontWeight: "unset !important",
    }),
    fixedWidth(pixelWidth: string) {
        return css({
            width: pixelWidth,
        });
    },
    autoColumn: css({
        width: "auto",
    }),
    tenantsCell: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
    }),
    footer: css({
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.secondary,
        margin: space[16],
        marginBottom: 0,
    }),
};
const columns = [
    { name: "Argo CD Applications", className: styles.fixedWidth("550px") },
    { name: "Environments", className: styles.autoColumn },
    { name: "Tenants", className: styles.fixedWidth("305px") },
];
interface PreviewDataContext {
    allProjects: ProjectResource[];
    allEnvironments: EnvironmentV2Resource[];
    allTenants: TenantResource[];
}
interface LoadedData {
    argoCDInstances: ArgoCDInstanceSummaryResource[];
    applications: ArgoCDApplicationResource[];
    context: PreviewDataContext;
}
type DrawerProps = {
    onShowGenerateAnnotationsDrawer: (selectedApplicationId: string, selectedApplicationSourceIndex: number | undefined) => void;
};
type ArgoCDDeploymentPreviewProps = DrawerProps & {
    spaceId: string;
    query: InlineStatusQuery<LoadedData, null>;
    showScopingHelpText: boolean;
};
export function ArgoCDDeploymentPreview(props: ArgoCDDeploymentPreviewProps) {
    const { spaceId, query, showScopingHelpText, ...rest } = props;
    const link = links.argoCDInstancesPage.generateUrl({ spaceId });
    return (<>
            <LegacyDataTable title="Argo CD Applications" aria-label="argo cd applications" className={tableStyles}>
                <LegacyDataTableHeader>
                    <LegacyDataTableRow>
                        {columns.map((column) => (<HeaderCell key={column.name} text={column.name} columnClassName={column.className}/>))}
                    </LegacyDataTableRow>
                </LegacyDataTableHeader>
                <LegacyDataTableBody>
                    {query.isLoading && <PreviewTableSkeleton />}
                    {query.error && (<LegacyDataTableRow>
                            <LegacyDataTableRowColumn colSpan={columns.length}>
                                <div>Error: {query.error.message}</div>
                            </LegacyDataTableRowColumn>
                        </LegacyDataTableRow>)}
                    {!query.isLoading && !query.error && query.result && (<ProjectSpecificArgoPreview argoInstances={query.result.argoCDInstances} applications={query.result.applications} context={query.result.context} refetch={query.refetch} {...rest}/>)}
                </LegacyDataTableBody>
            </LegacyDataTable>
            {showScopingHelpText && !query.isLoading && !query.error && (<p className={styles.footer}>
                    This list only shows Argo CD instances with applications scoped to this project. <InternalLink to={link}>View all instances</InternalLink>
                </p>)}
        </>);
}
type BasePreviewProps = DrawerProps & {
    context: PreviewDataContext;
    refetch: () => void;
};
type ProjectSpecificArgoPreviewProps = BasePreviewProps & {
    argoInstances: ArgoCDInstanceSummaryResource[];
    applications: ArgoCDApplicationResource[];
};
function ProjectSpecificArgoPreview(props: ProjectSpecificArgoPreviewProps) {
    const { argoInstances, applications, ...rest } = props;
    //not-distinct, but won't matter.
    const referencedArgoCdInstancesIDs = applications
        .map((app) => app.ArgoCDGatewayId)
        .values()
        .toArray();
    const referencedArgoCDInstances = argoInstances.filter((instance) => referencedArgoCdInstancesIDs.includes(instance.Id));
    return (<>
            {referencedArgoCDInstances.map((argoInstance, index) => {
            const apps = applications.filter((app) => app.ArgoCDGatewayId === argoInstance.Id);
            const hasProblemApps = applications.filter((app) => isApplicationMissingRequiredConfiguration(app, argoInstance)).length > 0;
            return <ArgoCDGatewayExpandableRow key={argoInstance.Id} argoInstance={argoInstance} applications={apps} startExpanded={hasProblemApps || index === 0} {...rest}/>;
        })}
        </>);
}
type ArgoCDGatewayExpandableRowProps = BasePreviewProps & {
    argoInstance: ArgoCDInstanceSummaryResource;
    applications: ArgoCDApplicationResource[];
    startExpanded: boolean;
};
export function ArgoCDGatewayExpandableRow(props: ArgoCDGatewayExpandableRowProps) {
    const { argoInstance, applications, startExpanded, ...rest } = props;
    const [isExpanded, setIsExpanded] = React.useState(startExpanded);
    return (<>
            <LegacyDataTableRow key={argoInstance.Id} className={styles.argoCDInstanceRow}>
                <LegacyDataTableRowColumn>
                    <div className={cx(styles.multiItemCellFlexContainer)}>
                        <span>
                            <DeprecatedIconButton onClick={() => setIsExpanded(!isExpanded)} icon={isExpanded ? "ChevronDownIcon" : "ChevronRightIcon"} accessibleName={`Toggle ${argoInstance.Id}`}/>
                        </span>
                        <span className={styles.argoCDNameField}>{argoInstance.Name}</span>
                    </div>
                </LegacyDataTableRowColumn>
                <LegacyDataTableRowColumn />
                <LegacyDataTableRowColumn />
            </LegacyDataTableRow>
            {isExpanded && (<>
                    {applications.map((app) => (<ArgoCDApplicationExpanderRow key={`application-${app.Name}`} argoInstance={argoInstance} application={app} showWarning={isApplicationMissingRequiredConfiguration(app, argoInstance)} {...rest}/>))}
                </>)}
        </>);
}
type ArgoCDApplicationRowProps = BasePreviewProps & {
    argoInstance: ArgoCDInstanceSummaryResource;
    application: ArgoCDApplicationResource;
    showWarning: boolean;
};
export function ArgoCDApplicationExpanderRow(props: ArgoCDApplicationRowProps) {
    const { argoInstance, application, showWarning, context, refetch, onShowGenerateAnnotationsDrawer } = props;
    const [isExpanded, setIsExpanded] = React.useState(showWarning);
    const links = argoInstance.WebUIUri ? new ArgoCDInstanceLinks(argoInstance.WebUIUri) : undefined;
    return (<>
            <LegacyDataTableRow>
                <LegacyDataTableRowColumn>
                    <div className={cx(styles.multiItemCellFlexContainer)}>
                        <span className={styles.indentedRow}>
                            <DeprecatedIconButton onClick={() => setIsExpanded(!isExpanded)} icon={isExpanded ? "ChevronDownIcon" : "ChevronRightIcon"} accessibleName={`Toggle ${application.Name}`}/>
                        </span>
                        <span className={styles.applicationNameFlex}>
                            <span className={styles.applicationName}>{application.Name}</span>
                            {links && <ExternalLink href={links.applicationDetails(application.Name, application.KubernetesNamespace)}>{""}</ExternalLink>}
                        </span>
                    </div>
                </LegacyDataTableRowColumn>
                <LegacyDataTableRowColumn className={styles.chipCell}>
                    <ArgoCDApplicationEnvironmentsTagList application={application} environments={context.allEnvironments}/>
                </LegacyDataTableRowColumn>
                <LegacyDataTableRowColumn className={styles.chipCell}>
                    <div className={styles.tenantsCell}>
                        <ArgoCDApplicationTenantsTagList application={application} tenants={context.allTenants}/>
                        {showWarning && (<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <ExclamationCircleIcon size={20} color={themeTokens.color.icon.warning}/>
                            </div>)}
                    </div>
                </LegacyDataTableRowColumn>
            </LegacyDataTableRow>
            {isExpanded && (<LegacyDataTableRow>
                    {/*The DataTable enforces bold text on FIRST element of column - which is not legal in this instance.*/}
                    <LegacyDataTableRowColumn fullWidth={true} fullHeight={true} colSpan={columns.length} className={styles.normalFont}>
                        <ArgoCDApplicationDetails application={application} onGitCredentialAdded={() => refetch()} onGenerateAnnotationsClick={(applicationSourceId) => onShowGenerateAnnotationsDrawer(application.Id, applicationSourceId)} projects={context.allProjects} environments={context.allEnvironments} tenants={context.allTenants} gateway={argoInstance}/>
                    </LegacyDataTableRowColumn>
                </LegacyDataTableRow>)}
        </>);
}
export const PreviewTableSkeleton = () => {
    const countSkeletonRow = 5;
    return (<>
            {Array.from({ length: countSkeletonRow }).map((_, j) => (<LegacyDataTableRow key={j}>
                    {Array.from({ length: columns.length }).map((_, i) => (<LegacyDataTableRowColumn key={i}>
                            <div className={styles.navItemIcon}>
                                <QuickSkeleton />
                            </div>
                        </LegacyDataTableRowColumn>))}
                </LegacyDataTableRow>))}
        </>);
};
function HeaderCell(props: {
    text: string;
    columnClassName: string;
}) {
    return (<LegacyDataTableRowColumn className={cx(styles.headerRow, props.columnClassName)}>
            <h2 className={styles.headerRow}>{props.text}</h2>
        </LegacyDataTableRowColumn>);
}
